<template>
	<div class="cassie-vertical-md">
		<SectionCard>
			<template #title>
				Details
			</template>
			<template #body>
				<v-row dense>
					<v-col cols="6">
						<TextField
							:value.sync="cookieBanner.name"
							label="Name *"
							:rules="{required: true, max: 255}"
							:disabled="!userCanCreateUpdate"
							@input="updateBanner('name', $event)"
						/>
					</v-col>
					<v-col cols="6">
						<Dropdown
							:value.sync="cookieBanner.brandId"
							:disabled="isEdit || cloneMode || !userCanCreateUpdate || !clientMultiBranded || singleBrand"
							:items="brandItems"
							custom-sort
							:label="'Brand *' | useLabels"
							searchable
							:rules="{ required: clientMultiBranded}"
							@input="updateBanner('brandId', $event)"
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<Dropdown
							:value.sync="cookieBanner.accessKeyId"
							:items="accessKeyItems"
							label="Access Key *"
							rules="required"
							:disabled="!userCanCreateUpdate"
							@input="updateBanner('accessKeyId', $event)"
						/>
					</v-col>
					<v-col
						v-if="userCanCreateUpdate && userCanCreateUpdateAccessKeys"
						cols="6"
					>
						<TextButton @click="showAccessKeyModal = true">
							<v-icon>
								mdi-plus
							</v-icon>
							Create
						</TextButton>
						<TextButton
							:disabled="!cookieBanner.accessKeyId"
							@click="openEditAccessKeyModal"
						>
							<v-icon>
								mdi-pencil
							</v-icon>
							Edit Selected Access Key
						</TextButton>
					</v-col>
					<v-col
						v-else-if="userCanSeeAccessKeys && cookieBanner.accessKeyId"
						cols="6"
					>
						<TextButton @click="openEditAccessKeyModal">
							<v-icon>
								mdi-eye
							</v-icon>
							View Selected Access Key
						</TextButton>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<Dropdown
							:value.sync="cookieBanner.privacyPolicyId"
							:items="activePrivacyPolicies"
							searchable
							custom-sort
							label="Privacy Policy *"
							rules="required"
							:disabled="!userCanCreateUpdate"
							@input="updateBanner('privacyPolicyId', $event)"
						/>
					</v-col>
					<v-col
						v-if="userCanCreateUpdate && userCanCreateUpdatePrivacyPolicies"
						cols="6"
					>
						<TextButton @click="showPrivacyPolicyModal = true">
							<v-icon>
								mdi-plus
							</v-icon>
							Create
						</TextButton>

						<TextButton
							:disabled="!cookieBanner.privacyPolicyId"
							@click="openEditPrivacyPolicyModal"
						>
							<v-icon>
								mdi-pencil
							</v-icon>
							Edit Selected Privacy Policy
						</TextButton>
					</v-col>
					<v-col
						v-else-if="userCanSeePrivacyPolicies && cookieBanner.privacyPolicyId"
						cols="6"
					>
						<TextButton @click="openEditPrivacyPolicyModal">
							<v-icon>
								mdi-eye
							</v-icon>
							View Selected Privacy Policy
						</TextButton>
					</v-col>
				</v-row>
				<div class="expiry-field">
					<TextField
						:value.sync="cookieBanner.cassieCookiesExpiryInDays"
						label="Cassie Cookies Expiry (in days) *"
						tooltip-text="Cookie expiry times are not guaranteed as they are dependent on specific browser behaviour."
						type="number"
						:rules="{required: true, numeric: true}"
						:disabled="!userCanCreateUpdate"
						@input="updateBanner('cassieCookiesExpiryInDays', $event)"
					/>
				</div>
				<QuestionCard
					title="Would you like to set a custom expiry for reject all cookies?"
					description="When enabled, consent cookies will expire after a custom number of days when users reject all, instead of using the standard expiry."
					show-yes-no-buttons
					class="pt-1"
					:value="rejectAllExpiryEnabled"
					:disabled="!userCanCreateUpdate"
					@input="onRejectAllExpiryToggle"
				>
					<div
						v-if="rejectAllExpiryEnabled"
						class="expiry-field"
					>
						<TextField
							:value.sync="cookieBanner.rejectAllCookiesExpiryInDays"
							label="Reject All Cookie Expiry (days) *"
							type="number"
							:rules="{required: true, numeric: true, min_value: 1}"
							:disabled="!userCanCreateUpdate"
							@input="updateBanner('rejectAllCookiesExpiryInDays', $event || null)"
						/>
					</div>
				</QuestionCard>
			</template>
		</SectionCard>
		<CookieBannerTemplateSelectorCards
			:cookie-banner="cookieBanner"
			:cookie-appearance-templates="cookieAppearanceTemplates"
			:cookie-text-templates="cookieTextTemplates"
			:cookie-collection-templates="cookieCollectionTemplates"
			:update-banner="updateBanner"
			:is-edit="isEdit"
			:clone-mode="cloneMode"
			:user-can-create-update="userCanCreateUpdate"
			:use-template="useTemplate"
			@show-cookie-text-manage-screen="$emit('show-cookie-text-manage-screen')"
			@open-edit-text-template-screen="$emit('open-edit-text-template-screen', $event)"
			@show-cookie-collection-manage-screen="$emit('show-cookie-collection-manage-screen')"
			@open-edit-collection-template-screen="$emit('open-edit-collection-template-screen', $event)"
			@show-cookie-appearance-manage-screen="$emit('show-cookie-appearance-manage-screen')"
			@open-edit-appearance-template-screen="$emit('open-edit-appearance-template-screen', $event)"
		/>
		<CookieBannerMspaSettingsCard
			v-if="isMspaEnabled"
			:cookie-banner="cookieBanner"
			:update-banner="updateBanner"
			:user-can-create-update="userCanCreateUpdate"
		/>
		<SectionCard>
			<template #title>
				Allowed Domains
			</template>
			<template #subtitle>
				Specify whether this Consent Banner can be used on all
				domains (potential security risk) or only on specific
				ones
			</template>
			<template #title-action>
				<Toggle
					:value.sync="cookieBanner.allowedDomainsValue"
					:toggle-labels="['Custom List', 'Allow All']"
					:disabled="!userCanCreateUpdate || cdcCookieCollectionSelected"
					class="mr-2"
					@update:value="updateBanner('allowedDomainsValue', $event)"
				/>
			</template>
			<template
				v-if="cookieBanner.allowedDomainsValue === false"
				#body
			>
				<OptionSelector
					v-if="showAddDomainSelector"
					:selected="cookieBanner.allowedDomains"
					entity-type="Domain"
					is-text
					:disabled="!userCanCreateUpdate"
					:required="cookieBanner.allowedDomains.length === 0"
					@update:selected="updateBanner('allowedDomains', $event)"
				/>
			</template>
		</SectionCard>
		<CookieBannerCisCard
			v-if="cassieIdentityServiceEnabled"
			:cookie-banner="cookieBanner"
			:cookie-banner-scripts="cookieBannerScripts"
			:update-banner="updateBanner"
			:cdc-cookie-collection-selected="cdcCookieCollectionSelected"
			:allowed-domains="cookieBanner.allowedDomains"
			:user-can-create-update="userCanCreateUpdate"
		/>
		<MetadataCard
			module-type="Consent Banner"
			:component-type-id="COMPONENT_TYPE.COOKIE_BANNERS"
			:assigned-meta-data="cookieBanner.metaData"
			:read-only="!userCanCreateUpdate"
			@update:metadata="updateBanner('metaData', $event)"
		/>
		<SectionCard v-if="showPreview">
			<template #title>
				Preview
			</template>
			<template #body>
				<p
					v-if="!isCookieBannerType"
					class="v-card__text"
				>
					Please note that the preview is not yet available for IAB or Service Banners.
				</p>
				<CookieBannerPreview
					v-else
					:cookie-appearance-template="selectedAppearanceTemplate"
					:cookie-text-template="selectedTextTemplate"
					:cookie-collection-template="selectedCollectionTemplate"
				/>
			</template>
		</SectionCard>
		<CookieBannerAccessKeyModal
			v-if="showAccessKeyModal"
			:brand-items="brandItems"
			:access-key-to-edit="accessKeyToEdit"
			:user-can-create-update="userCanCreateUpdateAccessKeys"
			@close="closeAccessKeyModal"
			@submit="submitAccessKey"
			@get-new-access-key-id="addNewAccessKeyToBanner"
		/>
		<CookieBannerPrivacyPolicyModal
			v-if="showPrivacyPolicyModal"
			:brand-items="brandItems"
			:privacy-policy-to-edit="privacyPolicyToEdit"
			:user-can-create-update="userCanCreateUpdatePrivacyPolicies"
			@close="closePrivacyPolicyModal"
			@submit="submitPrivacyPolicy"
			@get-new-privacy-policy-id="addNewPrivacyPolicyToBanner"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import Toggle from '../../../../../../../shared/components/toggle.vue'
import OptionSelector from '../../../../../../../shared/components/option-selector.vue'
import TextButton from '../../../../../../../shared/components/text-button.vue'
import MetadataCard from '../../../../shared/metadata-card.vue'
import CookieBannerCisCard from './cookie-banner-cis-card.vue'
import CookieBannerTemplateSelectorCards from './cookie-banner-template-selector-cards.vue'
import CookieBannerMspaSettingsCard from './cookie-banner-mspa-settings-card.vue'
import CookieBannerPreview from '../cookie-banner-preview/cookie-banner-preview.vue'
import CookieBannerAccessKeyModal from './cookie-banner-access-key-modal.vue'
import CookieBannerPrivacyPolicyModal from './cookie-banner-privacy-policy-modal.vue'
import { QuestionCard } from '../../../../../components/component-library-wrapper.js'
import { clientMultiBranded, brandOptions, UNIVERSAL_BRAND, UNIVERSAL_BRAND_ITEM, singleBrand } from '../../../../../../../shared/state/brands.js'
import { cassieIdentityServiceEnabled, privacyNoticeModuleEnabled } from '../../../../../../../shared/state/config-keys.js'
import { activePrivacyPolicies, updatePrivacyPolicies } from '../../../../../../../shared/state/privacy-policies.js'
import { headersAndFootersTypeIdEnum } from '../../../../../../../shared/enums/headers-and-footers.js'
import { COOKIE_COLLECTION_TEMPLATE } from '../cookie-appearances-templates/cookie-banner-preview-default-values.js'
import { COMPONENT_TYPE } from '../../../../../../../shared/state/meta-data.js'
import { setFormChangedValue } from '../../../../../../../shared/state/validation-observers.js'
import { ACCESS_KEYS_MODULE_FULL_PERMISSIONS, SEE_ACCESS_KEYS_MODULE, PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS, PRIVACY_POLICIES_MODULE } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { BANNER_TYPE } from '../cookie-appearances-templates/appearance-enums.js'
export default {
	components: {
		SectionCard,
		TextField,
		Dropdown,
		Toggle,
		OptionSelector,
		TextButton,
		MetadataCard,
		CookieBannerCisCard,
		CookieBannerTemplateSelectorCards,
		CookieBannerMspaSettingsCard,
		CookieBannerPreview,
		CookieBannerAccessKeyModal,
		CookieBannerPrivacyPolicyModal,
		QuestionCard
	},
	props: {
		cookieBanner: Object,
		isEdit: {
			type: Boolean,
			default: false
		},
		cloneMode: {
			type: Boolean,
			default: false
		},
		useTemplate: {
			type: Boolean,
			default: false
		},
		cookieTextTemplates: Array,
		cookieAppearanceTemplates: Array,
		cookieCollectionTemplates: Array,
		cookieScripts: Array,
		cookieCategories: Array,
		headers: Array,
		footers: Array,
		cookieAccessKeys: Array,
		cdcCookieCollectionSelected: Boolean,
		userCanCreateUpdate: Boolean
	},
	setup () {
		return {
			brandOptions,
			activePrivacyPolicies,
			cassieIdentityServiceEnabled,
			privacyNoticeModuleEnabled,
			headersAndFootersTypeIdEnum,
			COOKIE_COLLECTION_TEMPLATE,
			updatePrivacyPolicies,
			clientMultiBranded,
			singleBrand,
			COMPONENT_TYPE,
			setFormChangedValue
		}
	},
	data () {
		return {
			showAccessKeyModal: false,
			accessKeyToEdit: null,
			showPrivacyPolicyModal: false,
			privacyPolicyToEdit: null,
			showManageCollectionTemplateScreen: false,
			showManageTextTemplateScreen: false,
			cookieTextTemplateToEdit: null,
			showManageAppearanceTemplateScreen: false,
			rejectAllExpiryConfirmed: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdateAccessKeys () {
			return this.productAreaPermission(ACCESS_KEYS_MODULE_FULL_PERMISSIONS)
		},
		userCanSeeAccessKeys () {
			return this.productAreaPermission(SEE_ACCESS_KEYS_MODULE)
		},
		userCanCreateUpdatePrivacyPolicies () {
			if (this.privacyNoticeModuleEnabled) {
				return false
			}
			return this.productAreaPermission(PRIVACY_POLICIES_MODULE_FULL_PERMISSIONS)
		},
		userCanSeePrivacyPolicies () {
			return this.productAreaPermission(PRIVACY_POLICIES_MODULE)
		},
		brandItems () {
			// ensures when viewing universal object it displays in dropdown
			const containsUniversal = brandOptions.value.map(brand => {
				return brand.value
			}).includes(UNIVERSAL_BRAND)

			if (containsUniversal || !this.isEdit) {
				return brandOptions.value
			} else {
				return [
					UNIVERSAL_BRAND_ITEM,
					...brandOptions.value
				]
			}
		},
		accessKeyItems () {
			return this.cookieAccessKeys.map(accessKey => {
				return {
					text: accessKey.accessKeyName,
					value: accessKey.accessKeyId
				}
			}).sort((a, b) => a.text.localeCompare(b.text))
		},
		allowedDomainItems () {
			return [
				{ text: 'Allow All', value: true },
				{ text: 'Custom List', value: false }
			]
		},
		showAddDomainSelector () {
			return this.cookieBanner.allowedDomainsValue === false
		},
		cookieBannerScripts () {
			const cookieBannerScripts = this.cookieCollectionTemplates.find(({ id }) => id === this.cookieBanner.cookieCollectionTemplateId)?.cookieScripts
			if (cookieBannerScripts) {
				return cookieBannerScripts.map(script => {
					const matchingCookieScript = this.cookieScripts.find(({ id }) => id === script.id)
					return {
						...matchingCookieScript
					}
				})
			} else return []
		},
		showPreview () {
			return (
				this.selectedCollectionTemplate?.cookieCategories &&
        this.selectedTextTemplate &&
        this.selectedAppearanceTemplate
			)
		},
		isCookieBannerType () {
			return (
				this.selectedAppearanceTemplate &&
			this.selectedAppearanceTemplate.bannerType != null && this.selectedAppearanceTemplate.bannerType === BANNER_TYPE.COOKIE_BANNER
			)
		},

		selectedAppearanceTemplate () {
			return this.cookieAppearanceTemplates?.find(({ id }) => id === this.cookieBanner?.cookieAppearanceTemplateId)
		},
		selectedTextTemplate () {
			const textTemplate = this.cookieTextTemplates?.find(({ id }) => id === this.cookieBanner.cookieTextTemplateId)
			if (textTemplate) {
				if (textTemplate.headerId) textTemplate.headerHtml = this.headers?.find(({ id }) => id === textTemplate?.headerId)?.html
				if (textTemplate.footerId) textTemplate.footerHtml = this.footers?.find(({ id }) => id === textTemplate?.footerId)?.html
			}
			return textTemplate
		},
		selectedCollectionTemplate () {
			const collectionTemplate = this.cookieCollectionTemplates.find(({ id }) => id === this.cookieBanner.cookieCollectionTemplateId)
			const cookieCategories = collectionTemplate?.cookieCategories.map(category => ({
				name: this.cookieCategories?.find(({ id }) => id === category.id)?.categoryName,
				isStrictlyNecessary: this.cookieCategories?.find(({ id }) => id === category.id)?.isStrictlyCategory,
				description: this.cookieCategories?.find(({ id }) => id === category.id)?.displayText,
				cookies: collectionTemplate?.cookieScripts.map(script => ({
					name: this.cookieScripts?.find(({ id }) => id === script.id)?.cookieScriptName,
					cookieScriptType: this.cookieScripts?.find(({ id }) => id === script.id)?.cookieScriptType,
					description: this.cookieScripts?.find(({ id }) => id === script.id)?.displayText,
					gpcEnabled: this.cookieScripts?.find(({ id }) => id === script.id)?.gpcEnabled,
					categoryId: this.cookieScripts?.find(({ id }) => id === script.id)?.cookieCategoryId
				})).filter(({ categoryId }) => categoryId === category.id)
			}))
			return {
				cookieCategories: cookieCategories
			}
		},
		showManageCookieBannerScreen () {
			return !this.showManageCollectionTemplateScreen && !this.showManageTextTemplateScreen && !this.showManageAppearanceTemplateScreen
		},
		isMspaEnabled () {
			const selectedTemplate = this.cookieCollectionTemplates.find(({ id }) => id === this.cookieBanner.cookieCollectionTemplateId)
			return selectedTemplate?.mspaEnabled || false
		},
		rejectAllExpiryEnabled () {
			return this.rejectAllExpiryConfirmed || (this.cookieBanner.rejectAllCookiesExpiryInDays != null && this.cookieBanner.rejectAllCookiesExpiryInDays !== '')
		}
	},
	watch: {
		cdcCookieCollectionSelected (cdcCollection) {
			if (cdcCollection) this.updateBanner('allowedDomainsValue', false)
		}
	},
	methods: {

		updateBanner (property, value) {
			this.$emit('update:cookieBanner', {
				...this.cookieBanner,
				[property]: value
			})
			setFormChangedValue(true)
		},
		loadPrivacyPolicies () {
			updatePrivacyPolicies()
		},
		openEditAccessKeyModal () {
			this.showAccessKeyModal = true
			this.accessKeyToEdit = this.cookieAccessKeys.find(({ accessKeyId }) => accessKeyId === this.cookieBanner.accessKeyId)
		},
		closeAccessKeyModal () {
			this.showAccessKeyModal = false
			this.accessKeyToEdit = null
		},
		submitAccessKey () {
			this.closeAccessKeyModal()
			this.$emit('load-access-keys')
		},
		addNewAccessKeyToBanner (newAccessKeyId) {
			this.updateBanner('accessKeyId', newAccessKeyId)
		},
		openEditPrivacyPolicyModal () {
			this.showPrivacyPolicyModal = true
			const selectedPrivacyPolicy = activePrivacyPolicies.value.find(({ value }) => value === this.cookieBanner.privacyPolicyId)
			this.privacyPolicyToEdit = {
				privacyPolicyId: selectedPrivacyPolicy.value,
				privacyPolicyName: selectedPrivacyPolicy.text,
				privacyPolicyUrl: selectedPrivacyPolicy.policyUrl,
				externalVersionRef: selectedPrivacyPolicy.activeVersionName,
				isVisibleInCSPortal: selectedPrivacyPolicy.isVisibleInCSPortal,
				brandId: selectedPrivacyPolicy.brandId
			}
		},
		closePrivacyPolicyModal () {
			this.showPrivacyPolicyModal = false
			this.privacyPolicyToEdit = null
		},
		submitPrivacyPolicy () {
			this.closePrivacyPolicyModal()
			this.loadPrivacyPolicies()
		},
		addNewPrivacyPolicyToBanner (newPrivacyPolicyId) {
			this.updateBanner('privacyPolicyId', newPrivacyPolicyId)
		},
		onRejectAllExpiryToggle (enabled) {
			this.rejectAllExpiryConfirmed = enabled
			if (!enabled) {
				this.updateBanner('rejectAllCookiesExpiryInDays', null)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.expiry-field {
	max-width: 250px !important;
}
</style>
